import { ClientLambdaApi, CustomClient, ClientErAdminData } from '@/api-client.js'
import { downloadFile } from '@/app/shared/Services.js'
import ClientFileUpload from '@/app/shared/models/ClientFileUpload.js'
import ProductMasterProcessStatus from '@/app/admin/models/ProductMasterProcessStatus.js'
import UsersManagement from '@/app/admin/models/UsersManagement.js'
import Role from '@/app/admin/models/Role.js'
import LoadSequence from '@/app/shared/models/LoadSequence.js'
import ProductMappingInfo from '@/app/admin/models/ProductMappingInfo.js'
import ProductMapUrl from '@/app/admin/models/ProductMapUrl.js'

const getUrlProductMasterLegacy = async () => {
  const response = await ClientLambdaApi.get(
    '/generate-url-product-master-legacy/'
  )
  if (response.status === 200) {
    return new ProductMapUrl(response.data)
  }
  throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
}

const downloadProductMasterLegacy = async (url) => {
  const downloadProductMasterLegacyFilePromise = await CustomClient.get(
    url,
    { responseType: 'blob' })
  try {
    await downloadFile(downloadProductMasterLegacyFilePromise, 'Maestro de productos.xlsx')
  } catch (e) {
    throw new Error('Error al obtener el maestro de productos')
  }
}

const processProductMasterLegacy = async () => {
  const response = await ClientLambdaApi.get('/process-product-master-legacy')
  if (response.status >= 200 && response.status <= 202) {
    return response.data.map(rawProductMasterStatus => new ProductMasterProcessStatus(rawProductMasterStatus))
  }
  throw new Error('Error conectándose a lambda, comuníquese con su administrador')
}

const getProductMasterStatusProcess = async () => {
  const productMasterStatus = await ClientLambdaApi.get('/poll-product-master-status/')
  return productMasterStatus.data.map(rawProductMasterStatus => new ProductMasterProcessStatus(rawProductMasterStatus))
}

const getUsersManagement = async () => {
  const response = await ClientLambdaApi.get('/user-management?active=true')
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return new UsersManagement(response.data)
}

const updateUserRole = async (userId, roleId) => {
  const response = await ClientLambdaApi.put(`/user/${userId}/role/${roleId}`)
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  if (roleId !== response.data.role_id) {
    throw new Error('Error el rol no se ha actualizado correctamente, por favor intente nuevamente')
  }
  return new Role(response.data)
}

const postUser = async (user) => {
  const response = await ClientLambdaApi.post('/users', { user })
  if (response.status !== 201) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
}

const deactivateUser = async (userId) => {
  const response = await ClientLambdaApi.put(`/admin/users/${userId}/deactivate`)
  if (response.status !== 200) {
    throw new Error('Error al desactivar el usuario')
  }
  return response.data
}

const getProductMappingGroupByRetail = async () => {
  const response = await ClientLambdaApi.get('/product-map-retail-list')
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data.map(rawProductMappingInfo => new ProductMappingInfo(rawProductMappingInfo))
}

const getSignedProductMasterPostUrl = async () => {
  const response = await ClientLambdaApi.get('/sign-product-master-post-url')
  if (response.status !== 200) {
    throw new Error('Error al firmar la URL de subir productos')
  }
  return response.data
}

const postProductMasterFile = async (xlsxProductMasterFile, { fields, url }) => {
  const form = new FormData()
  Object.keys(fields).forEach((key) => {
    form.append(key, fields[key])
  })
  form.append('file', xlsxProductMasterFile)
  const response = await CustomClient.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } })

  if (response.status > 204) {
    throw new Error('Error intentando subir el archivo, por favor comunícate con el administrador')
  }
}

const getLoadStatus = async (granularity, startDate, endDate, metric, fact) => {
  const loadStates = await ClientErAdminData.get('/load-sales-status/', {
    params: {
      granularity,
      start_date: startDate,
      end_date: endDate,
      metric,
      fact
    }
  })
  return { columnDefs: loadStates.data.col_definition, data: loadStates.data.data }
}

const postCategory = async ({ name, parentCategoryId, levelId }) => {
  try {
    const newCategory = await ClientLambdaApi.post('/categories', {
      category: {
        name,
        parent_category_id: parentCategoryId,
        level_id: levelId
      }
    })
    return newCategory.data
  } catch (error) {
    throw Error(error.data.error)
  }
}

const updateLevelName = async (levelId, name) => {
  try {
    const response = await ClientLambdaApi.put(`/product-category-level/${levelId}/name`, {
      name
    })
    if (response.status !== 200) {
      throw new Error('Error al actualizar el nombre del nivel')
    }
    return response.data
  } catch (error) {
    throw Error(error.data?.error || 'Error al actualizar el nombre del nivel')
  }
}

const updateCategoryDescription = async (categoryId, description) => {
  try {
    const response = await ClientLambdaApi.put(`/product-category/${categoryId}/description`, {
      description
    })
    if (response.status !== 200) {
      throw new Error('Error al actualizar la descripción de la categoría')
    }
    return response.data
  } catch (error) {
    throw Error(error.data?.error || 'Error al actualizar la descripción de la categoría')
  }
}

const createProductCategoryClass = async (className) => {
  try {
    const response = await ClientLambdaApi.post('/product-category-class', {
      class_name: className
    })

    if (response.status !== 201) {
      throw new Error('Error al crear la nueva clase')
    }
    return response.data
  } catch (error) {
    // Check if it's an authentication error
    if (error.response?.status === 401) {
      throw Error('Sesión expirada. Por favor, inicia sesión nuevamente.')
    }

    throw Error(error.response?.data?.error || error.data?.error || 'Error al crear la nueva clase')
  }
}

const getLoadSequences = async (clientId) => {
  const rawLoadSequences = await ClientLambdaApi.get(`/client/${clientId}/load-sequences`, {
    params: {
      need_credential: true
    }
  })
  return rawLoadSequences.data.map(rawLoadSequence => new LoadSequence(rawLoadSequence))
}

const patchLoadSequence = async (clientId, loadSequenceId, username, password) => {
  const patchedLoadSequence = await ClientLambdaApi.patch(`/client/${clientId}/load-sequences/${loadSequenceId}`,
    { username, password })
  return patchedLoadSequence
}

const downloadPriceMaster = async (fileName, priceType) => {
  const download = require('js-file-download')
  const response = await ClientLambdaApi.get(`/download-price-master/?price_type=${priceType}`, {
    responseType: 'blob'
  })
  const ext = '.xlsx'
  const finalFileName = fileName + ext
  download(response.data, finalFileName)
}

const submitDownloadProductMasterJob = async () => {
  const postSubmitDownloadProductMasterJob = await ClientLambdaApi.post('/submit-download-product-master-job')
  return postSubmitDownloadProductMasterJob.data
}

const pollDownloadProductMasterJob = async (jobId) => {
  const pollDownloadProductMasterJob = await ClientLambdaApi.get(`/poll-job/${jobId}`)
  return pollDownloadProductMasterJob.data
}

const downloadProductMaster = async (url, name) => {
  const downloadProductMasterPromise = await CustomClient.get(
    url,
    { responseType: 'blob' })
  try {
    await downloadFile(downloadProductMasterPromise, name)
  } catch (e) {
    throw new Error('Error al obtener el maestro de productos')
  }
}

const createProduct = async (productData) => {
  const response = await ClientLambdaApi.post('/product-master/product', productData)
  if (response.data && response.data.exists) {
    const error = new Error('El producto ya existe')
    error.data = response.data
    throw error
  }
  return response.data
}

// Client Business Parameter API Services
const getRetailConfigs = async () => {
  const response = await ClientLambdaApi.get('/client-business-parameter/retail-configs')
  return response.data
}

const getRetailConfigById = async (retailId) => {
  const response = await ClientLambdaApi.get(`/client-business-parameter/retail-configs/${retailId}`)
  return response.data
}

const updateRetailConfig = async (retailId, configData) => {
  const response = await ClientLambdaApi.patch(`/client-business-parameter/retail-configs/${retailId}`, configData)
  return response.data
}

// New User Management API Services based on v1/admin endpoints
const getUsers = async (activeOnly = true, page = 1, perPage = 20) => {
  const response = await ClientLambdaApi.get('/admin/users', {
    params: {
      active_only: activeOnly,
      page,
      per_page: perPage
    }
  })
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const getUserById = async (userId) => {
  const response = await ClientLambdaApi.get(`/admin/users/${userId}`)
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const createUser = async (userData) => {
  const response = await ClientLambdaApi.post('/admin/users', { user: userData })
  if (response.status !== 201) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const updateUser = async (userId, userData) => {
  const response = await ClientLambdaApi.put(`/admin/users/${userId}`, { user: userData })
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const reactivateUser = async (userId) => {
  const response = await ClientLambdaApi.put(`/admin/users/${userId}/reactivate`)
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const getAvailableRoles = async () => {
  const response = await ClientLambdaApi.get('/admin/users/roles')
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const getAvailableModules = async () => {
  const response = await ClientLambdaApi.get('/admin/users/modules')
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const getUserPermissions = async (userId) => {
  const response = await ClientLambdaApi.get(`/admin/users/${userId}/permissions`)
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const updateUserPermissions = async (userId, permissions) => {
  const response = await ClientLambdaApi.put(`/admin/users/${userId}/permissions`, { permissions })
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const getUserNotificationPreferences = async (userId) => {
  const response = await ClientLambdaApi.get(`/admin/users/${userId}/notification-preferences`)
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const updateUserNotificationPreferences = async (userId, preferences) => {
  const response = await ClientLambdaApi.put(`/admin/users/${userId}/notification-preferences`, preferences)
  if (response.status !== 200) {
    throw new Error('Error interno en el servidor, por favor comunícate con el administrador')
  }
  return response.data
}

const getUserLoginHistory = async (userId, limit = 50) => {
  const response = await ClientLambdaApi.get(`/admin/users/${userId}/login-history`, {
    params: { limit }
  })
  if (response.status !== 200) {
    throw new Error('Error al obtener el historial de login del usuario')
  }
  return response.data
}

// Search users across all statuses
const searchUsers = async (searchQuery, limit = 10) => {
  const response = await ClientLambdaApi.get('/admin/users/search', {
    params: {
      q: searchQuery,
      limit
    }
  })
  return response.data
}

// Business Permissions API Services
const getCadenas = async () => {
  const response = await ClientLambdaApi.get('/client-business-parameter/retail-configs')
  return response.data
}

const getStoresByCadena = async (retailId) => {
  const response = await ClientLambdaApi.get(`/stores/by-retail/${retailId}`)
  return response.data
}

// ── Client File Upload ──

const initClientFileUpload = async (type, filenames, parameters = {}) => {
  const body = { type, files: filenames }
  if (Object.keys(parameters).length > 0) body.parameters = parameters
  const response = await ClientLambdaApi.post('/client-file-upload/init', body)
  if (response.status !== 201) throw new Error('Error al iniciar la subida de archivos')
  return new ClientFileUpload(response.data)
}

const uploadFileToS3 = (presignedUrl, file) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', presignedUrl)
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Error al subir el archivo ${file.name} a S3`))
    }
    xhr.onerror = () => reject(new Error(`Error de red al subir ${file.name}`))
    // Blob sin tipo → el browser no agrega Content-Type header
    xhr.send(new Blob([file]))
  })
}

const getClientFileUploadStatus = async (uploadId) => {
  const response = await ClientLambdaApi.get(`/client-file-upload/${uploadId}`)
  if (response.status === 404) throw new Error('Subida no encontrada')
  if (response.status !== 200) throw new Error('Error al obtener el estado de la subida')
  return new ClientFileUpload(response.data)
}

export {
  getUrlProductMasterLegacy,
  downloadProductMasterLegacy,
  processProductMasterLegacy,
  getProductMasterStatusProcess,
  getUsersManagement,
  updateUserRole,
  postUser,
  deactivateUser,
  getProductMappingGroupByRetail,
  getSignedProductMasterPostUrl,
  postProductMasterFile,
  getLoadStatus,
  postCategory,
  updateLevelName,
  updateCategoryDescription,
  createProductCategoryClass,
  getLoadSequences,
  patchLoadSequence,
  downloadPriceMaster,
  submitDownloadProductMasterJob,
  pollDownloadProductMasterJob,
  downloadProductMaster,
  createProduct,
  getRetailConfigs,
  getRetailConfigById,
  updateRetailConfig,
  getUsers,
  getUserById,
  createUser,
  updateUser,
  reactivateUser,
  getAvailableRoles,
  getAvailableModules,
  getUserPermissions,
  updateUserPermissions,
  getUserNotificationPreferences,
  updateUserNotificationPreferences,
  getUserLoginHistory,
  searchUsers,
  getCadenas,
  getStoresByCadena,
  initClientFileUpload,
  uploadFileToS3,
  getClientFileUploadStatus
}



// WEBPACK FOOTER //
// ./src/app/admin/Services.js